// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import type { ApiError } from '@/types'

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'statusCode' in error
  )
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  return 'An unexpected error occurred. Please try again.'
}

export function getFieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error) || !error.details) return {}
  return Object.fromEntries(
    Object.entries(error.details).map(([field, messages]) => [field, messages[0] ?? ''])
  )
}

const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  RATE_LIMITED: 'Too many requests. Please slow down.',
  PLAN_LIMIT_EXCEEDED: 'You have reached your plan limit. Upgrade to continue.',
  INVALID_CREDENTIALS: 'Invalid email or password.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',
}

export function friendlyError(error: unknown): string {
  if (isApiError(error)) {
    if (error.message?.trim()) return error.message
    if (error.code in ERROR_MESSAGES) return ERROR_MESSAGES[error.code] ?? error.message
  }
  return getErrorMessage(error)
}
